// DRAFT — not legal advice. Every factual assertion here must be confirmed by LAA and reviewed by counsel before this site accepts public traffic or runs paid advertising.
import { createFileRoute } from "@tanstack/react-router";

import { seo } from "@/lib/seo";
import { NAP, SHOW_PHONE } from "@/lib/business";
import { LegalPage, LegalSection } from "@/components/legal/LegalPage";

export const Route = createFileRoute("/_site/legal/accessibility")({
  head: () =>
    seo({
      title: "Accessibility Statement",
      description:
        "Our WCAG 2.2 Level AA conformance target, what is implemented on this site, known limitations, and how to report a barrier.",
      path: "/legal/accessibility",
    }),
  component: Page,
});

function Page() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Accessibility Statement"
      lastUpdated="4 September 2026"
      intro="We want this site to be usable by everyone, including people who browse with a keyboard, a screen reader, magnification, or reduced motion enabled."
    >
      <LegalSection title="Our commitment">
        <p>
          {NAP.legalName} treats accessibility as part of building the site properly, not as a
          feature added at the end. We build to the Web Content Accessibility Guidelines (WCAG) 2.2
          at <strong>Level AA</strong> as our conformance target, and we check new pages and
          components against that target before they go live.
        </p>
        <p>
          This is a statement of what we aim for and what we have done. It is not a claim of
          certification by a third party.
        </p>
      </LegalSection>

      <LegalSection title="What is implemented">
        <ul>
          <li>
            <strong>Semantic structure.</strong> Pages use real landmarks — header, navigation,
            main, footer — with a single h1 per page and headings nested in order, so screen reader
            users can navigate by structure rather than reading everything.
          </li>
          <li>
            <strong>Keyboard operability.</strong> Every interactive element can be reached and
            operated with a keyboard alone, in a logical order. Menus, accordions, and dialogs
            follow the expected WAI-ARIA keyboard patterns, dialogs trap focus and close on Escape,
            and a skip-to-content link is the first thing you reach on each page.
          </li>
          <li>
            <strong>Visible focus indicators.</strong> Focus is always visible, with a high-contrast
            ring that is not removed on any control.
          </li>
          <li>
            <strong>Colour contrast.</strong> Text and interface colours are chosen against measured
            contrast ratios that meet or exceed the AA thresholds on both our light and dark
            sections. Colour is never the only way information is conveyed — form errors, for
            example, carry text as well as colour.
          </li>
          <li>
            <strong>Reduced motion.</strong> All animation is inside a{" "}
            <code>prefers-reduced-motion</code> query. If your system asks for reduced motion, the
            site renders without movement, and content is never hidden waiting for an animation that
            will not run. Nothing autoplays, parallaxes, or flashes.
          </li>
          <li>
            <strong>Labelled form controls.</strong> Every field has a visible label that is
            programmatically associated with it, required fields are marked in text, and validation
            errors are announced through a polite live region next to the field they belong to.
          </li>
          <li>
            <strong>Text and zoom.</strong> Type is set in relative units and the layout reflows to
            400 percent zoom without loss of content or horizontal scrolling. Images that convey
            information carry descriptive alternative text; decorative images are hidden from
            assistive technology.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Known limitations">
        <p>Conformance is ongoing work, not a finished state. We are candid about the following:</p>
        <ul>
          <li>
            We have not completed a full independent audit of every page against every WCAG 2.2
            success criterion. Our checks combine automated testing with manual keyboard and screen
            reader review, and automated tools do not catch everything.
          </li>
          <li>
            Third-party embedded content — a scheduling calendar, an embedded video, an analytics or
            chat widget — is rendered by the vendor, and we do not control its markup. Those
            components may not fully conform. Where an embed presents a barrier we will provide an
            equivalent alternative; the booking page, for example, offers a plain accessible form as
            well as a calendar.
          </li>
          <li>
            Documents such as PDFs supplied during an engagement may not be fully tagged for
            accessibility. Ask us and we will provide the content in an accessible format.
          </li>
          <li>
            Older published content may not meet the current target. We fix these as we find them
            and when they are reported.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Report a barrier">
        <p>
          If any part of this site stops you doing what you came to do, tell us and we will fix it
          and help you in the meantime. Include the page address, what you were trying to do, and
          what happened, plus the browser and any assistive technology you use if you know it.
        </p>
        <ul>
          <li>
            Email <a href={`mailto:${NAP.email}`}>{NAP.email}</a>
          </li>
          {SHOW_PHONE ? (
            <li>
              Call <a href={NAP.phoneHref}>{NAP.phone}</a>, {NAP.hours}
            </li>
          ) : null}
          <li>
            Write to {NAP.legalName}, {NAP.street}, {NAP.city}, {NAP.region} {NAP.postalCode}
          </li>
        </ul>
        <p>
          We will acknowledge your report within two business days and tell you what we intend to do
          and when. If a fix will take time, we will offer another way to get the information or
          complete the task — including simply doing it with you over the phone or by email.
        </p>
      </LegalSection>

      <LegalSection title="Assessment and review">
        <p>
          This site was last reviewed for accessibility on 4 September 2026, using a combination of
          automated checks, keyboard-only navigation, screen reader spot checks, contrast
          measurement of every text and interface colour pair in our design system, and testing at
          400 percent zoom and at mobile widths. We review again whenever we ship a significant new
          page or component, and at least annually. This statement is updated at the same time.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
